/**
 * Clés de cache React Query.
 *
 * Toutes les clés d'un domaine partagent une racine, ce qui permet d'invalider
 * un domaine entier d'un seul appel : apres une commande, `queryKeys.cart.root`
 * et `queryKeys.orders.root` suffisent, sans énumérer chaque écran concerné.
 */
export const queryKeys = {
  session: {
    me: ['session', 'me'] as const,
    permissions: ['session', 'permissions'] as const,
    /** L'état du code de confidentialité : créé, essais restants, blocage. */
    pin: ['session', 'pin'] as const,
  },
  listings: {
    root: ['listings'] as const,
    feed: (filters: Record<string, unknown>) => ['listings', 'feed', filters] as const,
    detail: (id: string) => ['listings', 'detail', id] as const,
    comments: (id: string) => ['listings', 'comments', id] as const,
  },
  /** Le fil recommandé : une entrée par rayon, la graine vit dans la requête. */
  feed: {
    root: ['feed'] as const,
    forYou: (filters: Record<string, unknown>) => ['feed', 'for-you', filters] as const,
  },
  /**
   * Les directs de vente. La fiche d'un direct ne se relit pas en boucle : le
   * canal temps réel porte ses changements avec leur contenu.
   */
  lives: {
    root: ['lives'] as const,
    list: (filters: Record<string, unknown>) => ['lives', 'list', filters] as const,
    detail: (id: string) => ['lives', 'detail', id] as const,
    /** Rejoindre : compte le spectateur et branche la pièce. Jamais gardé en cache. */
    join: (id: string) => ['lives', 'join', id] as const,
  },
  /**
   * Racine à part, et non `listings.bookmarks` : mettre une annonce de côté ne
   * touche pas le fil, et le fil ne se recharge pas parce qu'on a mis une
   * annonce de côté. Deux caches, deux durées de vie.
   */
  bookmarks: {
    root: ['bookmarks'] as const,
  },
  communities: {
    root: ['communities'] as const,
    list: (filters: Record<string, unknown>) => ['communities', 'list', filters] as const,
    detail: (id: string) => ['communities', 'detail', id] as const,
    /** `status` distingue les membres de la file des demandes. */
    members: (id: string, status: string) => ['communities', 'members', id, status] as const,
    /** `scope` distingue le fil de la file de validation. */
    posts: (id: string, scope: string) => ['communities', 'posts', id, scope] as const,
    post: (postId: string) => ['communities', 'post', postId] as const,
    postComments: (postId: string) => ['communities', 'post-comments', postId] as const,
  },
  catalog: {
    root: ['catalog'] as const,
    categories: ['catalog', 'categories'] as const,
    /** Le référentiel des articles d'une catégorie ('' = toutes). */
    articles: (categoryId: string) => ['catalog', 'articles', categoryId] as const,
    products: (filters: Record<string, unknown>) => ['catalog', 'products', filters] as const,
    product: (id: string) => ['catalog', 'product', id] as const,
    /** Le code que recevra le prochain produit de cette boutique, dans cette catégorie. */
    nextCode: (shopId: string, categoryId: string) =>
      ['catalog', 'next-code', shopId, categoryId] as const,
  },
  shops: {
    root: ['shops'] as const,
    list: (filters: Record<string, unknown>) => ['shops', 'list', filters] as const,
    followed: ['shops', 'followed'] as const,
    detail: (id: string) => ['shops', 'detail', id] as const,
    members: (id: string) => ['shops', 'members', id] as const,
    sectors: (id: string) => ['shops', 'sectors', id] as const,
    subscription: (id: string) => ['shops', 'subscription', id] as const,
    dashboard: (id: string) => ['shops', 'dashboard', id] as const,
    /** Les mouvements, une entrée par jeu de filtres. */
    payments: (id: string, filters: Record<string, unknown> = {}) =>
      ['shops', 'payments', id, filters] as const,
    revenue: (id: string, period: Record<string, unknown>) =>
      ['shops', 'revenue', id, period] as const,
    audience: (id: string, period: Record<string, unknown>) =>
      ['shops', 'audience', id, period] as const,
  },
  /** Gestion des stocks (brique 5) : inventaire, fiche et journal d'un produit, totaux. */
  stock: {
    root: ['stock'] as const,
    summary: (shopId: string) => ['stock', 'summary', shopId] as const,
    overview: (shopId: string, filters: Record<string, unknown>) =>
      ['stock', 'overview', shopId, filters] as const,
    product: (productId: string) => ['stock', 'product', productId] as const,
    movements: (productId: string) => ['stock', 'movements', productId] as const,
  },
  cart: {
    root: ['cart'] as const,
  },
  orders: {
    root: ['orders'] as const,
    list: (filters: Record<string, unknown>) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
    /** Les commandes d'un panier scindé, à payer à la suite. */
    checkout: (checkoutId: string) => ['orders', 'checkout', checkoutId] as const,
    receipt: (id: string) => ['orders', 'receipt', id] as const,
    /** Contrôle d'une référence de transaction, une entrée par référence saisie. */
    paymentReference: (id: string, reference: string) =>
      ['orders', 'payment-reference', id, reference] as const,
  },
  /** Référentiel des opérateurs Mobile Money : il ne bouge qu'a la sortie d'un déploiement. */
  mobileMoney: {
    operators: ['mobile-money', 'operators'] as const,
  },
  urgentRequests: {
    root: ['urgent-requests'] as const,
    list: (filters: Record<string, unknown>) => ['urgent-requests', 'list', filters] as const,
    detail: (id: string) => ['urgent-requests', 'detail', id] as const,
  },
  disputes: {
    root: ['disputes'] as const,
    list: (filters: Record<string, unknown>) => ['disputes', 'list', filters] as const,
    detail: (id: string) => ['disputes', 'detail', id] as const,
  },
  inspections: {
    root: ['inspections'] as const,
    list: (filters: Record<string, unknown>) => ['inspections', 'list', filters] as const,
    detail: (id: string) => ['inspections', 'detail', id] as const,
  },
  /**
   * Files d'attente de l'administration. Elles étaient écrites en clair dans
   * chaque écran (`['admin', 'kyc', scope]`), donc invisibles a qui cherchait ce
   * qu'il faut rafraichir quand un dossier arrive : les voici au meme endroit
   * que le reste. Chaque file garde sa portée en dernier segment, et la racine
   * les invalide toutes.
   */
  admin: {
    root: ['admin'] as const,
    kyc: (scope: string) => ['admin', 'kyc', scope] as const,
    kycRoot: ['admin', 'kyc'] as const,
    reports: (scope: string) => ['admin', 'reports', scope] as const,
    reportsRoot: ['admin', 'reports'] as const,
    agents: (scope: string) => ['admin', 'agents', scope] as const,
    agentsRoot: ['admin', 'agents'] as const,
    certifiers: (zone: string) => ['admin', 'certifiers', zone] as const,
    /** L'annuaire des comptes, avec sa recherche et ses filtres. */
    accounts: (filters: Record<string, unknown>) => ['admin', 'accounts', filters] as const,
    /** Le dossier de modération d'un compte. */
    account: (id: string) => ['admin', 'account', id] as const,
    accountsRoot: ['admin', 'accounts'] as const,
  },
  /**
   * Le référentiel des secteurs d'activité. La liste sert au select de
   * l'ouverture de boutique (tout compte) ; les suggestions n'existent que
   * pour l'administration.
   */
  activitySectors: {
    root: ['activity-sectors'] as const,
    list: ['activity-sectors', 'list'] as const,
    suggestions: ['activity-sectors', 'suggestions'] as const,
  },
  platform: {
    root: ['platform'] as const,
    stats: ['platform', 'stats'] as const,
    settings: ['platform', 'settings'] as const,
    auditLogs: (filters: Record<string, unknown>) => ['platform', 'audit-logs', filters] as const,
    sanctions: (filters: Record<string, unknown>) => ['platform', 'sanctions', filters] as const,
  },
  messaging: {
    root: ['messaging'] as const,
    conversations: ['messaging', 'conversations'] as const,
    conversation: (id: string) => ['messaging', 'conversation', id] as const,
    messages: (id: string) => ['messaging', 'messages', id] as const,
    /** Contacts ajoutables à un groupe, filtrés par la recherche en cours. */
    contacts: (search: string) => ['messaging', 'contacts', search] as const,
  },
  /** Les appels : seul celui qui sonne ou qui dure se relit ; le reste arrive par le canal. */
  calls: {
    root: ['calls'] as const,
    detail: (id: string) => ['calls', 'detail', id] as const,
  },
  offers: {
    root: ['offers'] as const,
    detail: (id: string) => ['offers', 'detail', id] as const,
  },
  notifications: {
    root: ['notifications'] as const,
    list: (unreadOnly: boolean) => ['notifications', 'list', unreadOnly] as const,
    unread: ['notifications', 'unread'] as const,
  },
  billing: {
    root: ['billing'] as const,
    invoices: (filters: Record<string, unknown>) => ['billing', 'invoices', filters] as const,
    invoice: (id: string) => ['billing', 'invoice', id] as const,
    /** Le suivi de facturation d'une boutique. */
    summary: (shopId: string) => ['billing', 'summary', shopId] as const,
    plans: ['billing', 'plans'] as const,
    /** Le suivi du recouvrement de l'administration. */
    overview: ['billing', 'overview'] as const,
  },
  profile: {
    me: ['profile', 'me'] as const,
    kyc: ['profile', 'kyc'] as const,
    /** Mes candidatures d'agent : l'administration a les siennes, sous `admin`. */
    agentApplications: ['profile', 'agent-applications'] as const,
    googleLink: ['profile', 'google-link'] as const,
    paymentAccounts: (shopId?: string) => ['profile', 'payment-accounts', shopId ?? 'me'] as const,
    /** Les comptes de toutes les boutiques à la fois : ajouter un compte les rebat. */
    paymentAccountsRoot: ['profile', 'payment-accounts'] as const,
    public: (id: string) => ['profile', 'public', id] as const,
  },
};
